import { Box, Stack, styled, Tooltip, Typography } from '@chronosphereio/chrono-ui';
import { LineChart as EChartsLineChart, LineSeriesOption } from 'echarts/charts';
import { DatasetComponent, GridComponent, TitleComponent, TooltipComponent } from 'echarts/components';
import { EChartsCoreOption, use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useEffect, useMemo, useRef } from 'react';
import { ContentDimensions, FontSizeOption, formatValue, UnitOptions } from '../../core';
import { useChartsTheme } from '../context/ChartsProvider';
import { ChartApi, EChart } from '../EChart';
import { GraphSeries } from '../model/graph';
import { FillTextSpace } from '../utils/FillTextSpace';
import { useOptimalFontSize } from './calculateFontSize';

// eslint-disable-next-line react-hooks/rules-of-hooks
use([EChartsLineChart, GridComponent, DatasetComponent, TitleComponent, TooltipComponent, CanvasRenderer]);

const LINE_HEIGHT = 1.2;
const VALUE_FONT_WEIGHT = 700;
const MIN_SECONDARY_FONT_SIZE = 12;
// Fraction of heightForText allocated to each secondary element when auto-sizing.
// Value gets the remainder, giving it clear visual priority.
const SERIES_NAME_HEIGHT_RATIO = 0.12;
const DESCRIPTION_HEIGHT_RATIO = 0.12;
const MESSAGE_HEIGHT_RATIO = 0.18; // slightly more since message spans two lines
const DESCRIPTION_PADDING_BOTTOM = 8;
const PADDING_LEFT = 8;

export interface StatChartData {
  calculatedValue?: number | null;
  seriesData?: GraphSeries;
}

export interface StatChartComparison {
  seriesData?: GraphSeries;
  calculatedValue?: number | null;
  sparkline?: LineSeriesOption;
}

export type StatChartValueAlign = 'center' | 'left';

export interface StatChartBaseProps extends ContentDimensions {
  data: StatChartData;
  unit: UnitOptions;
  color?: string;
  sparkline?: LineSeriesOption;
  /**
   * Additional reference series to render alongside the primary sparkline. Used by the per-query
   * threshold feature when a `SingleValueTimeSeries` query is flagged as a threshold.
   */
  referenceSeries?: LineSeriesOption[];
  isMultiSeries?: boolean;
  valueFontSize?: FontSizeOption;
  /**
   * When true, the auto-computed value font is rounded down to the nearest even
   * integer (e.g. 13.4 → 12, 14.8 → 14) to reduce sub-pixel jitter on resize.
   * Ignored when `valueFontSize` is set. Defaults to continuous sizing.
   */
  roundValueFontDownToEven?: boolean;
  seriesNameFontSize?: FontSizeOption;
  descriptionFontSize?: FontSizeOption;
  messageFontSize?: FontSizeOption;
  comparison?: StatChartComparison;
  comparisonPeriodLabel?: string;
  description?: string;
  message?: string;
  backgroundColor?: string;
  hideValue?: boolean;
  showSeriesName?: boolean;
  /**
   * Determines the alignment of the value.
   * - `undefined` (default): The text will be aligned left when there is a sparkline. The text will be aligned center
   *   when there is not a sparkline.
   * - `'left'`: Always aligns the text left.
   * - `'center'`: Always aligns the text in the center.
   */
  valueAlign?: StatChartValueAlign;
}

/**
 * Use the context-aware `StatChart.Viz` instead.
 *
 * @deprecated
 */
export function StatChartBase(props: StatChartBaseProps) {
  const {
    width,
    height,
    data,
    unit,
    color,
    sparkline,
    referenceSeries,
    isMultiSeries,
    valueFontSize,
    roundValueFontDownToEven,
    seriesNameFontSize,
    descriptionFontSize,
    messageFontSize,
    comparison,
    comparisonPeriodLabel,
    description,
    message,
    backgroundColor,
    hideValue,
    showSeriesName: showSeriesNameProp,
    valueAlign: valueAlignProp,
  } = props;

  // The default functionality for show series is show it for multi and hide it for not.
  // If we are given an actual boolean value for the show series prop,then we will use that
  // to allow showing in single or hiding in multi
  const showSeriesName = showSeriesNameProp !== undefined ? showSeriesNameProp : isMultiSeries;

  const chartsTheme = useChartsTheme();
  const chartApiRef = useRef<ChartApi>();

  let formattedValue = '';
  if (data.calculatedValue === null) {
    formattedValue = 'null';
  } else if (typeof data.calculatedValue === 'number') {
    formattedValue = formatValue(data.calculatedValue, unit);
  }

  let formattedDelta = '';
  if (typeof data.calculatedValue === 'number' && typeof comparison?.calculatedValue === 'number') {
    const delta = (100 * (data.calculatedValue - comparison.calculatedValue)) / data.calculatedValue;
    formattedDelta = `(${delta > 0 ? '+' : ''}${formatValue(delta, { kind: 'Percent' })})`;
  }

  // If we are showing a sparkline, then we give the text half of the height
  const heightForText = sparkline ? height * 0.5 : height * 0.9;

  // Proportional height allocations for secondary elements. Only allocate when the element
  // is visible so hidden elements contribute nothing to the value's height budget.
  const seriesNameAlloc = showSeriesName ? heightForText * SERIES_NAME_HEIGHT_RATIO : 0;
  const descriptionAlloc = description ? heightForText * DESCRIPTION_HEIGHT_RATIO : 0;
  const messageAlloc = message ? heightForText * MESSAGE_HEIGHT_RATIO : 0;

  // Compute secondary font sizes first so their actual heights can inform the value's available space.
  // useOptimalFontSize is called unconditionally to satisfy hook rules; when alloc is 0 the
  // result is unused since the corresponding height below will also be 0.
  const optimalSeriesNameFontSize = useOptimalFontSize({
    text: data.seriesData?.name ?? '',
    fontSizeOverride: seriesNameFontSize,
    fontWeight: 400,
    width,
    height: seriesNameAlloc,
    lineHeight: LINE_HEIGHT,
    minSize: MIN_SECONDARY_FONT_SIZE,
  });

  const optimalDescriptionFontSize = useOptimalFontSize({
    text: description ?? '',
    fontSizeOverride: descriptionFontSize,
    fontWeight: 400,
    width,
    // Subtract padding so the full row cost (fontSize × lineHeight + padding) fits within the allocation.
    height: Math.max(descriptionAlloc - DESCRIPTION_PADDING_BOTTOM, 0),
    lineHeight: LINE_HEIGHT,
    minSize: MIN_SECONDARY_FONT_SIZE,
  });

  const optimalMessageFontSize = useOptimalFontSize({
    text: message ?? '',
    fontSizeOverride: messageFontSize,
    fontWeight: 400,
    width,
    // Message spans two lines; pass the per-line height after subtracting padding.
    height: Math.max((messageAlloc - DESCRIPTION_PADDING_BOTTOM) / 2, 0),
    lineHeight: LINE_HEIGHT,
    minSize: MIN_SECONDARY_FONT_SIZE,
  });

  const seriesNameHeight = showSeriesName ? optimalSeriesNameFontSize * LINE_HEIGHT : 0;
  const descriptionHeight = description ? optimalDescriptionFontSize * LINE_HEIGHT + DESCRIPTION_PADDING_BOTTOM : 0;
  // We let the message take up two rows, so it gets double line height
  const messageHeight = message ? optimalMessageFontSize * LINE_HEIGHT * 2 + DESCRIPTION_PADDING_BOTTOM : 0;
  // calculate value font size and height
  const valueTextHeight = heightForText - seriesNameHeight - descriptionHeight - messageHeight;

  const showValue = !hideValue || message === '' || message === undefined;

  const mainText = showValue ? `${formattedValue} ${formattedDelta}` : message;

  const fullTextSize = heightForText - seriesNameHeight;

  const hasSparkline = sparkline !== undefined;
  // The sparkline determines the default value alignment, which is used if the  `valueAlign` prop is not set.
  const defaultValueAlign: StatChartValueAlign = hasSparkline ? 'left' : 'center';
  const valueAlign = valueAlignProp ? valueAlignProp : defaultValueAlign;

  // We need to add padding when text is left aligned.
  const leftPadding = valueAlign === 'left' ? PADDING_LEFT : 0;

  const optimalValueFontSize = useOptimalFontSize({
    text: mainText,
    // override the font size if user selects it in the settings
    fontSizeOverride: valueFontSize,
    fontWeight: VALUE_FONT_WEIGHT,
    // for multiseries charts without sparkline, use only 70% of the available width so it looks better
    width: (isMultiSeries && !sparkline ? width * 0.7 : width) - leftPadding,
    // with sparkline, use only 50% of available height to leave room for chart
    // without sparkline, value should take up 90% of available space
    height: valueTextHeight,
    lineHeight: LINE_HEIGHT,
    roundDownToEven: roundValueFontDownToEven,
  });

  const option: EChartsCoreOption = useMemo(() => {
    if (data.seriesData === undefined) return chartsTheme.noDataOption;
    const hasBackground = backgroundColor !== undefined;

    const series = data.seriesData;
    const statSeries = [];
    if (sparkline !== undefined) {
      const lineSeries = {
        type: 'line',
        name: series.name,
        data: series.values,
        z: 1,
        symbol: 'none',
        animation: false,
        silent: true,
      };

      statSeries.push({ ...lineSeries, ...sparkline, name: series.name, data: series.values });

      // Push on a series to the bottom of the chart that is the default color to avoid the background
      // bleeding through the opaque fill
      const blankFillSeries = {
        type: 'line',
        name: 'blank-series',
        data: series.values,
        z: 0,
        symbol: 'none',
        animation: false,
        silent: true,

        lineStyle: {
          width: 0,
          color: 'transparent',
          opacity: 0,
        },
        areaStyle: {
          color: chartsTheme.theme.palette.background.container,
          opacity: 1,
        },
      };

      if (hasBackground) {
        statSeries.push(blankFillSeries);
      }

      if (comparison) {
        statSeries.push({
          ...lineSeries,
          ...comparison.sparkline,
          name: comparison.seriesData?.name,
          data: comparison.seriesData?.values,
        });

        if (hasBackground) {
          statSeries.push({
            ...blankFillSeries,
            name: 'blank-series-compare',
            data: comparison.seriesData?.values,
          });
        }
      }

      // Per-query threshold style series rendered on top of the primary sparkline.
      if (referenceSeries && referenceSeries.length > 0) {
        for (const refLine of referenceSeries) {
          statSeries.push({
            type: 'line',
            symbol: 'none',
            animation: false,
            silent: true,
            z: 2,
            ...refLine,
          });
        }
      }
    }

    const option = {
      title: {
        show: false,
      },
      grid: {
        show: false,
        top: '35%', // adds space above sparkline
        right: 0,
        bottom: 0,
        left: 0,
        containLabel: false,
      },
      xAxis: {
        type: 'time',
        show: false,
        boundaryGap: false,
      },
      yAxis: {
        type: 'value',
        show: false,
        min: (value: { min: number; max: number }) => {
          if (value.min >= 0 && value.min <= 1) {
            // helps with PercentDecimal units, or datasets that return 0 or 1 booleans
            return 0;
          }
          return value.min;
        },
      },
      tooltip: {
        show: false,
      },
      series: statSeries,
    };

    return option;
  }, [data, chartsTheme, sparkline, comparison, backgroundColor, referenceSeries]);

  useEffect(() => {
    if (chartApiRef.current) {
      chartApiRef.current.resize();
    }
  }, [showValue, message]);

  return (
    // Outer stack is for splitting the box
    <Box height={height} width={width} sx={{ backgroundColor: backgroundColor || 'inherit' }}>
      <Stack direction="column" height="100%" overflow="hidden">
        <Box flexGrow={1} flexBasis="50%">
          {
            // This stack is for displaying all the text
          }
          <Stack direction="column" height="100%">
            {
              // Series name if we need to show that
              // It will always need to be aligned to the start of the flex container
              // and it never needs to grow any
            }
            {showSeriesName && (
              <Box flexGrow={0} paddingLeft={1}>
                <SeriesName
                  variant="body1"
                  title={data.seriesData?.name}
                  color={color}
                  fontSize={optimalSeriesNameFontSize}
                >
                  {data.seriesData?.name}
                </SeriesName>
              </Box>
            )}

            {
              // This is the container that will have the value and description and message
            }
            <Box flexGrow={1}>
              {
                // We make this a stack so that we can control how the content is vertically
                // centered or not
                <Stack height="100%" justifyContent="center">
                  <div>
                    {showValue && (
                      <Stack
                        flexDirection="row"
                        alignItems="baseline"
                        paddingX={valueAlign === 'left' ? 1 : 0}
                        justifyContent={valueAlign === 'left' ? 'flex-start' : 'center'}
                      >
                        <Value variant="h3" color={color} fontSize={optimalValueFontSize}>
                          {formattedValue}
                        </Value>
                        {comparisonPeriodLabel && formattedDelta ? (
                          <Tooltip Content={`Compared to ${comparisonPeriodLabel}`}>
                            <Delta fontSize={optimalValueFontSize * 0.4} color={color}>
                              {formattedDelta}
                            </Delta>
                          </Tooltip>
                        ) : (
                          formattedDelta && (
                            <Delta fontSize={optimalValueFontSize * 0.4} color={color}>
                              {formattedDelta}
                            </Delta>
                          )
                        )}
                      </Stack>
                    )}

                    {!showValue && (
                      <Box width={width} height={fullTextSize}>
                        <FillTextSpace
                          height={fullTextSize}
                          width={width}
                          color={color}
                          minFontSize={messageFontSize ?? 20}
                          maxFontSize={messageFontSize}
                        >
                          {message}
                        </FillTextSpace>
                      </Box>
                    )}

                    {description && (
                      <Description title={description} textAlign={valueAlign} fontSize={optimalDescriptionFontSize}>
                        {description}
                      </Description>
                    )}
                    {message && showValue && (
                      <Message title={message} color={color} textAlign={valueAlign} fontSize={optimalMessageFontSize}>
                        {message}
                      </Message>
                    )}
                  </div>
                </Stack>
              }
            </Box>
          </Stack>
        </Box>

        {sparkline !== undefined && (
          <Box flexGrow={1} flexBasis="50%">
            <EChart
              chartApiRef={chartApiRef}
              width={width}
              height={height * 0.5}
              option={option}
              theme={chartsTheme.echartsTheme}
              renderer="svg"
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
}

const SeriesName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'fontSize',
})<{ color?: string; fontSize?: number }>(({ color, fontSize }) => ({
  color: color,
  fontSize: `${fontSize}px`,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: LINE_HEIGHT,
}));

const Value = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'fontSize',
})<{ color?: string; fontSize?: number }>(({ theme, color, fontSize }) => ({
  color: color ?? theme.palette.text.primary,
  fontSize: `${fontSize}px`,
  whiteSpace: 'nowrap',
  lineHeight: LINE_HEIGHT,
}));

const Delta = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'fontSize',
})<{ color?: string; fontSize?: number }>(({ theme, fontSize, color }) => ({
  color: color ?? theme.palette.text.secondary,
  fontSize: `${fontSize}px`,
  whiteSpace: 'nowrap',
  lineHeight: LINE_HEIGHT,
}));

const Description = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'fontSize',
})<{ fontSize?: number }>(({ theme, fontSize }) => ({
  fontSize: `${fontSize}px`,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: LINE_HEIGHT,
  color: theme.palette.text.secondary,
  width: '100%',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  paddingBottom: `${DESCRIPTION_PADDING_BOTTOM}px`,
}));

const Message = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'fontSize',
})<{ color?: string; fontSize?: number }>(({ theme, color, fontSize }) => ({
  color: color ?? theme.palette.text.primary,
  fontSize: `${fontSize}px`,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: LINE_HEIGHT,
  // These Webkit specific properites are a supported
  // pattern for creating text overflow after x lines
  // This is used in the case a message is very long, it will
  // ellipsis after the 2nd line
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: '2',
  width: '100%',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));
